/**
 * CITY HEAD DASHBOARD - Sales Intelligence Dashboard
 * ===================================================
 * Unified metrics from Booking Master Engine
 * 
 * Sections:
 * 1. Today Focus - Real-time daily metrics
 * 2. Manager-wise Funnel Health
 * 3. Referral Performance
 * 4. VDNB Recovery Engine
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../components/ui/select';
import { 
  Calendar, Users, Building2, IndianRupee, TrendingUp, TrendingDown,
  Clock, Target, Award, AlertTriangle, ArrowRight, RefreshCw,
  ChevronRight, UserCheck, UserX, Percent, 
  Filter, BarChart3, PieChart, X
} from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../utils/api';
import UnifiedBookingTable from '../components/UnifiedBookingTable';
import VisitsList from '../components/VisitsList';
import LostReviewPool from '../components/LostReviewPool';
import ManagerFunnelTable from '../components/ManagerFunnelTable';
import { MobileMenuTrigger } from '../components/Layout';

// === METRIC CARD COMPONENT ===
const MetricCard = ({ 
  label, 
  value, 
  icon: Icon, 
  color = 'slate', 
  onClick, 
  trend,
  suffix = '',
  clickable = false 
}) => {
  const colorClasses = {
    slate: 'bg-slate-50 text-slate-700',
    emerald: 'bg-emerald-50 text-emerald-700',
    blue: 'bg-blue-50 text-blue-700',
    amber: 'bg-amber-50 text-amber-700',
    purple: 'bg-purple-50 text-purple-700',
    cyan: 'bg-cyan-50 text-cyan-700',
    red: 'bg-red-50 text-red-700',
  };
  
  return (
    <div 
      className={`p-4 rounded-xl border ${colorClasses[color]} ${clickable ? 'cursor-pointer hover:shadow-md transition-all' : ''}`}
      onClick={clickable ? onClick : undefined}
    >
      <div className="flex items-center justify-between mb-2">
        <div className={`p-2 rounded-lg bg-white/80`}>
          <Icon size={18} className="text-current" />
        </div>
        {trend !== undefined && (
          <div className={`flex items-center gap-1 text-xs ${trend >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
            {trend >= 0 ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
            {Math.abs(trend)}%
          </div>
        )}
      </div>
      <p className="text-2xl font-bold">{value}{suffix}</p>
      <p className="text-xs text-current/70 mt-1">{label}</p>
      {clickable && (
        <p className="text-xs text-current/50 mt-2 flex items-center gap-1">
          Click to view <ChevronRight size={12} />
        </p>
      )}
    </div>
  );
};

// === MAIN COMPONENT ===
const CityHeadDashboard = ({ user }) => {
  const navigate = useNavigate();
  const location = window.location;
  
  // Check if user is CRM Support (simplified interface)
  const isCrmSupport = user?.role === 'crm_support';
  
  // Parse URL params for tab selection (for redirect from deprecated booking-verification)
  const urlParams = new URLSearchParams(location.search);
  const tabFromUrl = urlParams.get('tab');
  
  // State - CRM Support always starts at bookings tab
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [period, setPeriod] = useState('mtd');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [activeSection, setActiveSection] = useState(isCrmSupport ? 'bookings' : (tabFromUrl || 'overview')); // CRM Support always sees bookings
  const [managerFilter, setManagerFilter] = useState(null); // For filtering bookings by manager
  const [visitFilters, setVisitFilters] = useState({}); // For filtering visits list
  const [bookingFilters, setBookingFilters] = useState({}); // For filtering bookings list
  const [pipelineData, setPipelineData] = useState(null); // Pipeline Health data
  const [loadingPipeline, setLoadingPipeline] = useState(false);
  const [lostReviewCount, setLostReviewCount] = useState(0); // Lost Review Pool count
  
  // Fetch dashboard data
  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const params = { period };
      if (period === 'custom' && startDate && endDate) {
        params.start_date = startDate;
        params.end_date = endDate;
      }
      
      const response = await api.getCityHeadDashboard(params);
      setDashboardData(response.data);
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, [period, startDate, endDate]);
  
  // Fetch Pipeline Health data
  const fetchPipelineData = useCallback(async () => {
    setLoadingPipeline(true);
    try {
      const params = { period };
      if (period === 'custom' && startDate && endDate) {
        params.start_date = startDate;
        params.end_date = endDate;
      }
      const response = await api.getBookingFunnelDashboard(params);
      setPipelineData(response.data);
    } catch (error) {
      console.error('Failed to fetch pipeline data:', error);
    } finally {
      setLoadingPipeline(false);
    }
  }, [period, startDate, endDate]);
  
  // Fetch Lost Review Pool count for badge
  const fetchLostReviewCount = useCallback(async () => {
    try {
      const response = await api.getLostReviewPool({ limit: 1 });
      if (response.data) {
        setLostReviewCount(response.data.aging_metrics?.total || 0);
      }
    } catch (error) {
      console.error('Failed to fetch lost review count:', error);
    }
  }, []);
  
  // Fetch pipeline data when tab is active
  useEffect(() => {
    if (activeSection === 'pipeline' && !pipelineData) {
      fetchPipelineData();
    }
  }, [activeSection, pipelineData, fetchPipelineData]);
  
  useEffect(() => {
    fetchDashboard();
    fetchLostReviewCount();
  }, [fetchDashboard, fetchLostReviewCount]);
  
  if (loading || !dashboardData) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="flex items-center gap-2 text-slate-500">
          <div className="w-6 h-6 border-2 border-slate-300 border-t-slate-600 rounded-full animate-spin" />
          Loading dashboard...
        </div>
      </div>
    );
  }
  
  const { today_focus, manager_funnel_health, referral_performance, vdnb_recovery } = dashboardData;
  
  return (
    <div className="max-w-7xl mx-auto p-4 sm:p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-2">
          {/* Mobile Hamburger Menu */}
          <MobileMenuTrigger user={user} className="-ml-2" />
          <div>
            <h1 className="text-2xl font-bold text-slate-900">
              {isCrmSupport ? 'Booking Journey' : 'Sales Intelligence Dashboard'}
            </h1>
            <p className="text-slate-500">
              {isCrmSupport ? 'Manage booking lifecycle and verifications' : `City: ${dashboardData.city || 'All'}`}
            </p>
          </div>
        </div>
        
        {/* Period selector hidden for CRM Support - they don't need analytics filters */}
        {!isCrmSupport && (
          <div className="flex items-center gap-3">
            {/* Period Selector */}
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className="w-[130px]">
                <Calendar size={14} className="mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="today">Today</SelectItem>
                <SelectItem value="mtd">MTD</SelectItem>
                <SelectItem value="qtd">QTD</SelectItem>
                <SelectItem value="ytd">YTD</SelectItem>
                <SelectItem value="custom">Custom</SelectItem>
              </SelectContent>
            </Select>
            
            {/* Refresh */}
            <Button variant="outline" size="sm" onClick={fetchDashboard}>
              <RefreshCw size={14} />
            </Button>
          </div>
        )}
      </div>
      
      {/* Section Tabs - CRM Support only sees Booking Journey */}
      <div className="flex items-center gap-2 overflow-x-auto pb-2">
        {(isCrmSupport ? [
          // CRM Support sees only the booking table
          { key: 'bookings', label: 'Booking Journey', icon: Building2 },
        ] : [
          // Admin/City Head/Strategy sees all analytics tabs
          { key: 'overview', label: 'Overview', icon: BarChart3 },
          { key: 'managers', label: 'Manager Funnel', icon: Users },
          { key: 'referrals', label: 'Referrals', icon: Award },
          { key: 'vdnb', label: 'VDNB Recovery', icon: AlertTriangle },
          { key: 'pipeline', label: 'Pipeline Health', icon: TrendingUp },
          { key: 'bookings', label: 'All Bookings', icon: Building2 },
          { key: 'lost_review', label: 'Lost Review', icon: UserX, badge: lostReviewCount },
        ]).map(tab => (
          <Button
            key={tab.key}
            variant={activeSection === tab.key ? 'default' : 'outline'}
            size="sm"
            onClick={() => setActiveSection(tab.key)}
            className={`${activeSection === tab.key ? 'bg-slate-900' : ''} relative`}
            data-testid={`tab-${tab.key}`}
          >
            <tab.icon size={14} className="mr-1" />
            {tab.label}
            {tab.badge > 0 && (
              <span className="absolute -top-1.5 -right-1.5 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                {tab.badge > 99 ? '99+' : tab.badge}
              </span>
            )}
          </Button>
        ))}
      </div>
      
      {/* ===================== SECTION 1: TODAY FOCUS ===================== */}
      {activeSection === 'overview' && (
        <>
          <Card className="p-4">
            <div className="flex items-center gap-2 mb-4">
              <div className="p-2 bg-amber-100 rounded-lg">
                <Clock size={18} className="text-amber-700" />
              </div>
              <div>
                <h2 className="font-semibold text-slate-900">Today Focus</h2>
                <p className="text-xs text-slate-500">Real-time daily metrics</p>
              </div>
            </div>
            
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
              <MetricCard
                label="Visits Scheduled"
                value={today_focus.visits_scheduled_today}
                icon={Calendar}
                color="blue"
                clickable
                onClick={() => {
                  setVisitFilters({ date: 'today', status: 'SCHEDULED' });
                  setActiveSection('visits');
                }}
              />
              <MetricCard
                label="Visits Completed"
                value={today_focus.visits_completed_today}
                icon={UserCheck}
                color="emerald"
                clickable
                onClick={() => {
                  setVisitFilters({ date: 'today', status: 'COMPLETED' });
                  setActiveSection('visits');
                }}
              />
              <MetricCard
                label="Revisits"
                value={today_focus.revisits_today}
                icon={RefreshCw}
                color="cyan"
                clickable
                onClick={() => {
                  setVisitFilters({ date: 'today', visit_type: 'revisit' });
                  setActiveSection('visits');
                }}
              />
              <MetricCard
                label="Bookings Today"
                value={today_focus.bookings_today}
                icon={Building2}
                color="purple"
                clickable
                onClick={() => {
                  setBookingFilters({ booking_date: 'today' });
                  setManagerFilter(null);
                  setActiveSection('bookings');
                }}
              />
              <MetricCard
                label={`High AV (>${today_focus.high_av_threshold_lacs}L)`}
                value={today_focus.high_av_visits_today}
                icon={IndianRupee}
                color="amber"
                clickable
                onClick={() => {
                  // High AV threshold is typically 200L (2Cr)
                  setBookingFilters({ av_min: today_focus.high_av_threshold_lacs || 200 });
                  setManagerFilter(null);
                  setActiveSection('bookings');
                }}
              />
            </div>
          </Card>
          
          {/* Quick Stats Summary */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Manager Performance Summary */}
            <Card className="p-4 lg:col-span-2">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-slate-900">Top Managers by AV</h3>
                <Button variant="ghost" size="sm" onClick={() => setActiveSection('managers')}>
                  View All <ChevronRight size={14} />
                </Button>
              </div>
              <div className="space-y-2">
                {manager_funnel_health.slice(0, 5).map((manager, idx) => (
                  <div 
                    key={manager.manager_id} 
                    className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 cursor-pointer"
                    onClick={() => setActiveSection('managers')}
                  >
                    <div className="flex items-center gap-3">
                      <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                        idx === 0 ? 'bg-amber-100 text-amber-700' :
                        idx === 1 ? 'bg-slate-200 text-slate-600' :
                        idx === 2 ? 'bg-orange-100 text-orange-700' :
                        'bg-slate-100 text-slate-500'
                      }`}>
                        {idx + 1}
                      </span>
                      <div>
                        <p className="font-medium text-sm text-slate-900">{manager.manager_name}</p>
                        <p className="text-xs text-slate-500">{manager.bookings} bookings • {manager.visit_to_booking_pct}% conv</p>
                      </div>
                    </div>
                    <p className="font-bold text-emerald-700">₹{manager.net_av_lacs}L</p>
                  </div>
                ))}
              </div>
            </Card>
            
            {/* Referral & VDNB Quick Stats */}
            <div className="space-y-4">
              {/* Referral Summary */}
              <Card className="p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Award size={18} className="text-purple-600" />
                  <h3 className="font-semibold text-slate-900">Referral Summary</h3>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="text-center p-3 bg-purple-50 rounded-lg">
                    <p className="text-xl font-bold text-purple-700">{referral_performance.referral_bookings}</p>
                    <p className="text-xs text-slate-500">Bookings</p>
                  </div>
                  <div className="text-center p-3 bg-emerald-50 rounded-lg">
                    <p className="text-xl font-bold text-emerald-700">₹{referral_performance.referral_net_av_lacs}L</p>
                    <p className="text-xs text-slate-500">Net AV</p>
                  </div>
                </div>
                <Button variant="ghost" size="sm" className="w-full mt-3" onClick={() => setActiveSection('referrals')}>
                  View Details <ChevronRight size={14} />
                </Button>
              </Card>
              
              {/* VDNB Summary */}
              <Card className="p-4">
                <div className="flex items-center gap-2 mb-3">
                  <AlertTriangle size={18} className="text-amber-600" />
                  <h3 className="font-semibold text-slate-900">VDNB Recovery</h3>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="text-center p-3 bg-amber-50 rounded-lg">
                    <p className="text-xl font-bold text-amber-700">{vdnb_recovery.total_vdnb}</p>
                    <p className="text-xs text-slate-500">Total VDNB</p>
                  </div>
                  <div className="text-center p-3 bg-emerald-50 rounded-lg">
                    <p className="text-xl font-bold text-emerald-700">{vdnb_recovery.vdnb_to_booking_pct}%</p>
                    <p className="text-xs text-slate-500">Recovery %</p>
                  </div>
                </div>
                <Button variant="ghost" size="sm" className="w-full mt-3" onClick={() => setActiveSection('vdnb')}>
                  View Details <ChevronRight size={14} />
                </Button>
              </Card>
            </div>
          </div>
        </>
      )}
      
      {/* ===================== SECTION 2: MANAGER FUNNEL ===================== */}
      {activeSection === 'managers' && (
        <ManagerFunnelTable defaultPeriod={period} showFilters={true} />
      )}
      
      
      {/* ===================== SECTION 3: REFERRALS ===================== */}
      {activeSection === 'referrals' && (
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-slate-900">Referral Performance</h2>
          
          <Card className="p-6">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
              <MetricCard
                label="Total Referral Customers"
                value={referral_performance.total_referral_customers}
                icon={Users}
                color="purple"
              />
              <MetricCard
                label="Referral Visits"
                value={referral_performance.referral_visits}
                icon={Calendar}
                color="blue"
              />
              <MetricCard
                label="Referral Bookings"
                value={referral_performance.referral_bookings}
                icon={Building2}
                color="emerald"
              />
              <MetricCard
                label="Referral Net AV"
                value={`₹${referral_performance.referral_net_av_lacs}`}
                suffix="L"
                icon={IndianRupee}
                color="amber"
              />
              <MetricCard
                label="Conversion %"
                value={referral_performance.referral_conversion_pct}
                suffix="%"
                icon={Percent}
                color="cyan"
              />
              <MetricCard
                label="Customer vs Employee"
                value={`${referral_performance.customer_referral_bookings}/${referral_performance.employee_referral_bookings}`}
                icon={PieChart}
                color="slate"
              />
            </div>
          </Card>
          
          {/* Referral Split */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card className="p-4">
              <h3 className="font-semibold text-slate-900 mb-3">Customer Referrals</h3>
              <div className="flex items-center justify-between p-4 bg-purple-50 rounded-xl">
                <div>
                  <p className="text-3xl font-bold text-purple-700">{referral_performance.customer_referral_bookings}</p>
                  <p className="text-sm text-slate-600">Bookings</p>
                </div>
                <div className="text-right">
                  <p className="text-xl font-bold text-emerald-700">₹{referral_performance.customer_referral_av_lacs}L</p>
                  <p className="text-sm text-slate-600">AV</p>
                </div>
              </div>
            </Card>
            <Card className="p-4">
              <h3 className="font-semibold text-slate-900 mb-3">Employee Referrals</h3>
              <div className="flex items-center justify-between p-4 bg-blue-50 rounded-xl">
                <div>
                  <p className="text-3xl font-bold text-blue-700">{referral_performance.employee_referral_bookings}</p>
                  <p className="text-sm text-slate-600">Bookings</p>
                </div>
                <div className="text-right">
                  <p className="text-xl font-bold text-emerald-700">₹{referral_performance.employee_referral_av_lacs}L</p>
                  <p className="text-sm text-slate-600">AV</p>
                </div>
              </div>
            </Card>
          </div>
        </div>
      )}
      
      {/* ===================== SECTION 4: VDNB ===================== */}
      {activeSection === 'vdnb' && (
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-slate-900">VDNB Recovery Engine</h2>
          
          <Card className="p-6">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <MetricCard
                label="Total VDNB"
                value={vdnb_recovery.total_vdnb}
                icon={UserX}
                color="red"
              />
              <MetricCard
                label="Added in Period"
                value={vdnb_recovery.vdnb_added_in_period}
                icon={TrendingDown}
                color="amber"
              />
              <MetricCard
                label="Recovered in Period"
                value={vdnb_recovery.vdnb_recovered_in_period}
                icon={TrendingUp}
                color="emerald"
              />
              <MetricCard
                label="Recovery %"
                value={vdnb_recovery.vdnb_to_booking_pct}
                suffix="%"
                icon={Target}
                color="cyan"
              />
            </div>
          </Card>
          
          {/* Manager VDNB Breakdown */}
          <Card className="p-4">
            <h3 className="font-semibold text-slate-900 mb-3">Manager-wise VDNB %</h3>
            <div className="space-y-2">
              {vdnb_recovery.manager_vdnb_breakdown?.map(manager => (
                <div key={manager.manager_id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <span className="font-medium text-slate-700">{manager.manager_name}</span>
                  <span className={`font-bold ${manager.vdnb_pct > 30 ? 'text-red-600' : manager.vdnb_pct > 20 ? 'text-amber-600' : 'text-emerald-600'}`}>
                    {manager.vdnb_pct}%
                  </span>
                </div>
              ))}
            </div>
          </Card>
        </div>
      )}
      
      {/* ===================== SECTION 5: PIPELINE HEALTH ===================== */}
      {activeSection === 'pipeline' && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-slate-900">Post-Approval Pipeline Health</h2>
            <Button variant="outline" size="sm" onClick={fetchPipelineData} disabled={loadingPipeline}>
              <RefreshCw size={14} className={loadingPipeline ? 'animate-spin' : ''} />
            </Button>
          </div>
          
          {loadingPipeline ? (
            <Card className="p-8">
              <div className="flex items-center justify-center">
                <div className="w-6 h-6 border-2 border-slate-300 border-t-slate-600 rounded-full animate-spin" />
                <span className="ml-3 text-slate-600">Loading pipeline data...</span>
              </div>
            </Card>
          ) : !pipelineData ? (
            <Card className="p-8 text-center">
              <AlertTriangle size={48} className="mx-auto text-slate-300 mb-3" />
              <p className="text-slate-500">No pipeline data available</p>
            </Card>
          ) : (
            <>
              {/* Funnel Stages */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                {pipelineData.funnel_stages?.map((stage, idx) => (
                  <Card key={stage.stage} className={`p-4 border-l-4 ${
                    idx === 0 ? 'border-l-blue-500' :
                    idx === 1 ? 'border-l-indigo-500' :
                    idx === 2 ? 'border-l-purple-500' :
                    idx === 3 ? 'border-l-amber-500' :
                    'border-l-emerald-500'
                  }`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-medium text-slate-500 uppercase">{stage.stage}</span>
                      {stage.conversion_rate !== undefined && stage.conversion_rate < 100 && (
                        <Badge variant="outline" className="text-xs">
                          {stage.conversion_rate}%
                        </Badge>
                      )}
                    </div>
                    <p className="text-2xl font-bold text-slate-900">{stage.count}</p>
                    <p className="text-sm text-slate-600">₹{stage.value_lacs?.toFixed(1) || 0}L</p>
                    {stage.pending_count > 0 && (
                      <p className="text-xs text-amber-600 mt-1">
                        {stage.pending_count} pending
                      </p>
                    )}
                  </Card>
                ))}
              </div>
              
              {/* Conversion Summary */}
              <Card className="p-4">
                <h3 className="font-semibold text-slate-700 mb-4">Conversion Flow</h3>
                <div className="flex items-center justify-between overflow-x-auto gap-2 pb-2">
                  {pipelineData.funnel_stages?.map((stage, idx) => (
                    <React.Fragment key={stage.stage}>
                      <div className="flex flex-col items-center min-w-[100px]">
                        <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white text-sm font-bold ${
                          idx === 0 ? 'bg-blue-500' :
                          idx === 1 ? 'bg-indigo-500' :
                          idx === 2 ? 'bg-purple-500' :
                          idx === 3 ? 'bg-amber-500' :
                          'bg-emerald-500'
                        }`}>
                          {stage.count}
                        </div>
                        <span className="text-xs text-slate-600 mt-2 text-center">{stage.stage}</span>
                      </div>
                      {idx < (pipelineData.funnel_stages?.length || 0) - 1 && (
                        <div className="flex items-center min-w-[40px]">
                          <ArrowRight size={20} className="text-slate-400" />
                          <span className="text-xs text-slate-500 ml-1">
                            {pipelineData.funnel_stages[idx + 1]?.conversion_rate || 0}%
                          </span>
                        </div>
                      )}
                    </React.Fragment>
                  ))}
                </div>
              </Card>
              
              {/* Key Metrics */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <Card 
                  className="p-4 bg-blue-50 border-blue-200 cursor-pointer hover:shadow-md transition-all"
                  onClick={() => {
                    // Navigate to All Bookings with APPROVED filter using booking_date (standard sales date)
                    // This ensures consistency with Pipeline Health counts
                    setBookingFilters({ status: 'APPROVED' });
                    setManagerFilter(null);
                    setActiveSection('bookings');
                  }}
                >
                  <p className="text-sm text-blue-600 font-medium">Total Approved</p>
                  <p className="text-2xl font-bold text-blue-700">{pipelineData.funnel_stages?.[0]?.count || 0}</p>
                  <p className="text-xs text-blue-600">₹{pipelineData.funnel_stages?.[0]?.value_lacs?.toFixed(1) || 0}L</p>
                  <p className="text-xs text-blue-500 mt-1 flex items-center gap-1">
                    Click to view <ChevronRight size={12} />
                  </p>
                </Card>
                <Card className="p-4 bg-purple-50 border-purple-200">
                  <p className="text-sm text-purple-600 font-medium">Registered</p>
                  <p className="text-2xl font-bold text-purple-700">{pipelineData.funnel_stages?.[2]?.count || 0}</p>
                  <p className="text-xs text-purple-600">₹{pipelineData.funnel_stages?.[2]?.value_lacs?.toFixed(1) || 0}L</p>
                </Card>
                <Card className="p-4 bg-amber-50 border-amber-200">
                  <p className="text-sm text-amber-600 font-medium">Invoiced</p>
                  <p className="text-2xl font-bold text-amber-700">{pipelineData.funnel_stages?.[3]?.count || 0}</p>
                  <p className="text-xs text-amber-600">₹{pipelineData.funnel_stages?.[3]?.value_lacs?.toFixed(1) || 0}L</p>
                </Card>
                <Card className="p-4 bg-emerald-50 border-emerald-200">
                  <p className="text-sm text-emerald-600 font-medium">Revenue Received</p>
                  <p className="text-2xl font-bold text-emerald-700">{pipelineData.funnel_stages?.[4]?.count || 0}</p>
                  <p className="text-xs text-emerald-600">₹{pipelineData.revenue_summary?.total_revenue_lacs?.toFixed(1) || 0}L</p>
                </Card>
              </div>
            </>
          )}
        </div>
      )}
      
      {/* ===================== SECTION 6: VISITS LIST ===================== */}
      {activeSection === 'visits' && (
        <VisitsList
          filters={visitFilters}
          city={dashboardData?.city || ''}
          onBack={() => {
            setVisitFilters({});
            setActiveSection('overview');
          }}
          showHeader={true}
        />
      )}
      
      {/* ===================== SECTION 6: ALL BOOKINGS ===================== */}
      {activeSection === 'bookings' && (
        <div className="space-y-4">
          {/* Show filter indicators */}
          {(managerFilter || bookingFilters.booking_date || bookingFilters.av_min || bookingFilters.status) && (
            <div className="flex flex-wrap items-center gap-2 p-3 bg-purple-50 border border-purple-200 rounded-lg">
              <Filter size={16} className="text-purple-600" />
              <span className="text-sm text-purple-700">Active Filters:</span>
              
              {bookingFilters.status === 'APPROVED' && (
                <Badge variant="outline" className="bg-white border-emerald-300 text-emerald-700">
                  Status: Approved
                </Badge>
              )}
              
              {managerFilter && (
                <Badge variant="outline" className="bg-white">
                  Manager: {manager_funnel_health?.find(m => m.manager_id === managerFilter)?.manager_name || managerFilter}
                </Badge>
              )}
              
              {bookingFilters.booking_date === 'today' && (
                <Badge variant="outline" className="bg-white">
                  Booking Date: Today
                </Badge>
              )}
              
              {bookingFilters.av_min && (
                <Badge variant="outline" className="bg-white">
                  Min AV: ₹{bookingFilters.av_min}L+
                </Badge>
              )}
              
              {bookingFilters.date_field === 'created_at' && (
                <Badge variant="outline" className="bg-white text-slate-500">
                  By Entry Date
                </Badge>
              )}
              
              <Button
                variant="ghost"
                size="sm"
                className="ml-auto h-6 px-2 text-purple-600 hover:text-purple-800"
                onClick={() => {
                  setManagerFilter(null);
                  setBookingFilters({});
                }}
              >
                <X size={14} className="mr-1" />
                Clear all filters
              </Button>
            </div>
          )}
          <UnifiedBookingTable
            role={user?.role || "city_head"}
            title="All Bookings"
            defaultFilters={{ 
              period: bookingFilters.booking_date === 'today' ? 'today' : period, 
              manager_id: managerFilter || undefined,
              av_min: bookingFilters.av_min || undefined,
              status: bookingFilters.status || undefined,
              date_field: bookingFilters.date_field || undefined
            }}
            pageSize={20}
          />
        </div>
      )}

      {/* ===================== SECTION 7: LOST REVIEW POOL ===================== */}
      {activeSection === 'lost_review' && (
        <LostReviewPool 
          city={dashboardData.city} 
          onRefresh={() => {
            fetchLostReviewCount();
            fetchDashboard();
          }}
        />
      )}
    </div>
  );
};

export default CityHeadDashboard;
